import { useEffect, useRef } from "react";
import content from "./content.json";
import { orderedOptions } from "./reportModel";

/**
 * Brief §5/§7 — one question at a time, native radios (4 fixed-order
 * options), explicit Next/Back (select never auto-submits), Back disabled
 * on Q1, Next becomes "See my result" on Q7. Heading gets focus on
 * question change so screen readers announce the new question (§12
 * accessibility). Exit/overview is now `DiagnosticLayout`'s shared
 * top-right control, not owned by this screen.
 */
export const QuestionScreen = ({
  language,
  questionIndex,
  totalQuestions,
  selectedOptionId,
  onSelect,
  onNext,
  onBack,
  storageOk,
}) => {
  const ui = content.content[language].ui;
  const question = content.content[language].questions[questionIndex];
  const options = orderedOptions(questionIndex, language);
  const isLast = questionIndex === totalQuestions - 1;
  const headingRef = useRef(null);

  useEffect(() => {
    headingRef.current?.focus();
  }, [questionIndex]);

  return (
    <div className="max-w-2xl mx-auto px-5 md:px-8 py-10 md:py-16" data-testid="pa-question-screen">
      <p className="text-xs font-semibold uppercase tracking-wide text-[#6b6b63] text-right mb-6" data-testid="pa-progress-label">
        {ui.question} {questionIndex + 1} {ui.of} {totalQuestions}
      </p>

      <div className="w-full h-1.5 rounded-full bg-black/10 mb-1.5" aria-hidden="true">
        <div
          className="h-1.5 rounded-full bg-[#b74e33] transition-all"
          style={{ width: `${((questionIndex + 1) / totalQuestions) * 100}%` }}
        />
      </div>
      {!storageOk && (
        <p className="text-[11px] text-[#9b792b] mb-4" data-testid="pa-storage-unavailable">
          {ui.storageUnavailable}
        </p>
      )}
      {storageOk && <p className="text-[11px] text-[#9a968a] mb-4">{ui.local}</p>}

      <h2
        ref={headingRef}
        tabIndex={-1}
        className="font-serif text-2xl md:text-3xl text-[#263b3d] leading-snug mb-7 outline-none"
        data-testid="pa-question-text"
      >
        {question.title}
      </h2>

      <fieldset className="space-y-3 mb-8">
        <legend className="sr-only">{question.title}</legend>
        {options.map((option, i) => {
          const checked = selectedOptionId === option.id;
          return (
            <label
              key={option.id}
              data-testid={`pa-option-${option.id}`}
              className={`flex items-start gap-3 rounded-xl border px-5 py-4 cursor-pointer transition-colors ${
                checked ? "bg-[#263b3d] border-[#263b3d] text-white" : "bg-white border-black/10 text-[#263b3d] hover:border-[#b74e33]/40"
              }`}
            >
              <input
                type="radio"
                name={`pa-question-${questionIndex}`}
                value={option.id}
                checked={checked}
                onChange={() => onSelect(option.id)}
                className="mt-1 shrink-0"
                data-testid={`pa-option-radio-${option.id}`}
              />
              <span
                className={`shrink-0 w-6 h-6 rounded-full border flex items-center justify-center text-xs font-medium ${
                  checked ? "border-white/60 text-white" : "border-black/20 text-[#6b6b63]"
                }`}
              >
                {String.fromCharCode(65 + i)}
              </span>
              <span className="text-sm md:text-base">{option.label}</span>
            </label>
          );
        })}
      </fieldset>

      {!selectedOptionId && (
        <p className="text-sm text-[#9b792b] mb-4" data-testid="pa-select-hint">
          {ui.select}
        </p>
      )}

      <div className="flex items-center justify-between">
        {questionIndex > 0 ? (
          <button
            type="button"
            data-testid="pa-back-button"
            onClick={onBack}
            className="font-body text-sm text-[#6b6b63] underline-offset-4 hover:text-[#b74e33] hover:underline transition-colors"
          >
            &larr; {ui.back}
          </button>
        ) : (
          <span />
        )}
        <button
          type="button"
          data-testid="pa-next-button"
          onClick={onNext}
          disabled={!selectedOptionId}
          className="px-7 py-3 rounded-full font-semibold text-sm text-white bg-[#b74e33] hover:bg-[#9c3f29] disabled:opacity-40 transition-colors"
        >
          {isLast ? ui.finish : ui.next}
        </button>
      </div>
    </div>
  );
};

export default QuestionScreen;
